import { Theme } from "@mui/material";
import {
  ActivityCounts,
  CategoryBreakdown,
  SegmentBreakdown,
  SegmentKey,
  SubtypeBreakdown,
  WorkItemSubtype,
} from "../../../store/services/salesManagerService";
import { WorkItemTypeEnum } from "../../../store/types/work.types";

// ---------- Labels ----------

// Svenska etiketter för de 8 finkorniga segmenten + Total.
export const SEGMENT_LABELS: Record<SegmentKey | "Total", string> = {
  SuspectProspect: "Suspekt / Prospekt",
  Pipeline: "Pipeline",
  ExistingCustomer: "Befintlig kund",
  CrossSellOpportunity: "Merförsäljning",
  Churning: "Lämnar",
  FormerCustomer: "Före detta",
  NetworkOnly: "Endast nät",
  Unknown: "Oklassificerad",
  Total: "Totalt",
};

// Stabil visningsordning — matris, donut, drawer följer alla denna.
// Går från tidigt i säljcykeln (Suspect) → aktiv kund → avslut → okänt.
export const SEGMENT_ORDER: SegmentKey[] = [
  "SuspectProspect",
  "Pipeline",
  "ExistingCustomer",
  "CrossSellOpportunity",
  "Churning",
  "FormerCustomer",
  "NetworkOnly",
  "Unknown",
];

export const CATEGORY_LABELS = {
  Communication: "Planering",
  Followup: "Mötesbokning",
  Total: "Totalt",
} as const;

export const SUBTYPE_LABELS: Record<WorkItemSubtype, string> = {
  [WorkItemSubtype.Email]: "Email",
  [WorkItemSubtype.PhoneCall]: "Telefon",
  [WorkItemSubtype.Sms]: "SMS",
  [WorkItemSubtype.DigitalMeeting]: "Digitalt möte",
  [WorkItemSubtype.PhysicalMeeting]: "Fysiskt möte",
  [WorkItemSubtype.Admin]: "Administration",
  [WorkItemSubtype.Other]: "Övrigt",
  [WorkItemSubtype.ContactAttempt]: "Kontaktförsök",
};

// Mappning från subtyp → WorkItemTypeEnum för att återanvända befintliga
// icon/color-helpers i workItemUtils. Other saknar en 1:1-matchning och
// mappas till NotSet som ger generic ikon/grå färg i helpers.
export const SUBTYPE_TO_WORK_ITEM_TYPE: Record<WorkItemSubtype, WorkItemTypeEnum> =
  {
    [WorkItemSubtype.Email]: WorkItemTypeEnum.Email,
    [WorkItemSubtype.PhoneCall]: WorkItemTypeEnum.PhoneCall,
    [WorkItemSubtype.Sms]: WorkItemTypeEnum.Sms,
    [WorkItemSubtype.DigitalMeeting]: WorkItemTypeEnum.DigitalMeeting,
    [WorkItemSubtype.PhysicalMeeting]: WorkItemTypeEnum.PhysicalMeeting,
    [WorkItemSubtype.Admin]: WorkItemTypeEnum.Admin,
    [WorkItemSubtype.Other]: WorkItemTypeEnum.NotSet,
    [WorkItemSubtype.ContactAttempt]: WorkItemTypeEnum.ContactAttempt,
  };

// Ordnad lista av subtyper för konsistent rendering i alla matriser/charts.
// Denna är den fullständiga listan — använd kategori-specifika versioner
// nedan när du vill filtrera bort subtyper som inte hör hemma i en kategori.
export const SUBTYPE_ORDER: WorkItemSubtype[] = [
  WorkItemSubtype.Email,
  WorkItemSubtype.PhoneCall,
  WorkItemSubtype.Sms,
  WorkItemSubtype.DigitalMeeting,
  WorkItemSubtype.PhysicalMeeting,
  WorkItemSubtype.Admin,
  WorkItemSubtype.Other,
];

// Planering (Communication): inga möten — möten är per definition avtalade
// med kunden och hör hemma i Mötesbokning.
export const SUBTYPE_ORDER_COMMUNICATION: WorkItemSubtype[] = [
  WorkItemSubtype.Email,
  WorkItemSubtype.PhoneCall,
  WorkItemSubtype.Sms,
  WorkItemSubtype.Admin,
  WorkItemSubtype.Other,
];

// Mötesbokning (Followup): inga admin-uppgifter — admin är intern planering.
export const SUBTYPE_ORDER_FOLLOWUP: WorkItemSubtype[] = [
  WorkItemSubtype.Email,
  WorkItemSubtype.PhoneCall,
  WorkItemSubtype.Sms,
  WorkItemSubtype.DigitalMeeting,
  WorkItemSubtype.PhysicalMeeting,
  WorkItemSubtype.Other,
];

// ---------- Accessors ----------

// Tom cell — fallback när backend saknar en nyare subtyp (Admin/ContactAttempt
// kan saknas tills respektive deploy landat).
const EMPTY_COUNTS: ActivityCounts = {
  Total: 0,
  Completed: 0,
  Skipped: 0,
  Active: 0,
  Overdue: 0,
  CompletionRate: 0,
};

// Plocka rätt subtyp-cell ur en SubtypeBreakdown
export const getSubtypeCounts = (
  breakdown: SubtypeBreakdown,
  subtype: WorkItemSubtype
): ActivityCounts => {
  switch (subtype) {
    case WorkItemSubtype.Email:
      return breakdown.Email ?? EMPTY_COUNTS;
    case WorkItemSubtype.PhoneCall:
      return breakdown.PhoneCall ?? EMPTY_COUNTS;
    case WorkItemSubtype.Sms:
      return breakdown.Sms ?? EMPTY_COUNTS;
    case WorkItemSubtype.DigitalMeeting:
      return breakdown.DigitalMeeting ?? EMPTY_COUNTS;
    case WorkItemSubtype.PhysicalMeeting:
      return breakdown.PhysicalMeeting ?? EMPTY_COUNTS;
    case WorkItemSubtype.Admin:
      return breakdown.Admin ?? EMPTY_COUNTS;
    case WorkItemSubtype.Other:
      return breakdown.Other ?? EMPTY_COUNTS;
    case WorkItemSubtype.ContactAttempt:
      return breakdown.ContactAttempt ?? EMPTY_COUNTS;
  }
};

// Antal kontaktförsök (effort-metrik) — ligger utanför Total och visas
// som en fristående sidokolumn, aldrig som del av en summa.
// Defensiv: backend kan sakna fältet tills ContactAttempt-deployen landat.
export const getContactAttemptCount = (
  breakdown: SubtypeBreakdown
): number => breakdown.ContactAttempt?.Total ?? 0;

// Totalraden (summa av alla segment, kategorier och subtyper) — motsvarar
// de gamla platta TotalActivities/CompletedActivities etc.
export const getGrandTotal = (breakdown: SegmentBreakdown): ActivityCounts =>
  breakdown.Total.Total.Total;

// Totalt per segment (fortfarande summerat över kategorier + subtyper)
export const getSegmentTotal = (
  breakdown: SegmentBreakdown,
  segment: SegmentKey | "Total"
): ActivityCounts => breakdown[segment].Total.Total;

// Summerar två ActivityCounts-celler och räknar om CompletionRate på totalen.
// Skipped räknas bort från både täljare och nämnare så raten speglar
// "andel av icke-skippade som faktiskt genomförts".
export const sumActivityCounts = (
  a: ActivityCounts,
  b: ActivityCounts
): ActivityCounts => {
  const total = a.Total + b.Total;
  const completed = a.Completed + b.Completed;
  const skipped = (a.Skipped ?? 0) + (b.Skipped ?? 0);
  const eligibleTotal = total - skipped;
  const eligibleCompleted = completed - skipped;
  return {
    Total: total,
    Completed: completed,
    Skipped: skipped,
    Active: a.Active + b.Active,
    Overdue: a.Overdue + b.Overdue,
    CompletionRate:
      eligibleTotal > 0
        ? Math.round((eligibleCompleted / eligibleTotal) * 1000) / 10
        : 0,
  };
};

// Totalt per kategori inom ett segment
export const getCategoryTotal = (
  category: CategoryBreakdown,
  bucket: "Communication" | "Followup" | "Total"
): ActivityCounts => category[bucket].Total;

// Totalt för Planering (Communication) över alla segment
export const getPlaneringTotal = (breakdown: SegmentBreakdown): ActivityCounts =>
  breakdown.Total.Communication.Total;

// Totalt för Mötesbokning (Followup) över alla segment
export const getMotesbokningTotal = (
  breakdown: SegmentBreakdown
): ActivityCounts => breakdown.Total.Followup.Total;

// ---------- Färger / formatters ----------

export const getCompletionRateColor = (
  rate: number,
  theme: Theme
): string => {
  if (rate >= 80) return theme.palette.success.main;
  if (rate >= 60) return theme.palette.warning.main;
  return theme.palette.error.main;
};

export const formatPercent = (value: number, decimals = 1): string =>
  `${value.toFixed(decimals)}%`;

// Summerad "overdue + backlog" som säljchefens "skuld-KPI"
export const getBacklogTotal = (breakdown: SegmentBreakdown): number =>
  breakdown.Total.Total.Total.Overdue + breakdown.CarriedOverBacklog;

// Delad tooltip-förklaring för "Backlog" / CarriedOverBacklog-fältet.
// Visas på alla ställen siffran dyker upp så säljchefen slipper gissa.
// Matchar backend: ScheduledTo < startDate + Active → räknas här, aldrig i matrisen.
export const BACKLOG_TOOLTIP =
  "Backlog = öppna aktiviteter som var schemalagda FÖRE periodens start men fortfarande inte är gjorda. De släpas in i vald period och räknas inte i matrisen — en indikator på gammal skuld som halkat efter.";

// Delad tooltip-förklaring för "Overdue".
// Matchar backend: bara items schemalagda INOM perioden (de före startDate har
// redan plockats ut som Backlog) vars ScheduledTo < now. Mäts mot NU, inte mot
// periodens slut — därför meningsfull främst för den pågående perioden. För en
// historisk period ligger "now" långt efter endDate, så nästan allt öppet inom
// perioden ser ut som overdue.
export const OVERDUE_TOOLTIP =
  "Overdue = öppna aktiviteter som var schemalagda inom vald period men vars tid redan passerat. Mäts mot dagens datum (inte periodens slut), så siffran är mest meningsfull för den pågående perioden — tittar du på en avslutad period framstår nästan allt öppet som overdue. Aktiviteter schemalagda före perioden räknas inte här utan som Backlog.";

// Kombinerad förklaring för den sammanslagna "Overdue + backlog"-skuld-KPI:n.
// De är ömsesidigt uteslutande: ett item är antingen overdue (schemalagt inom
// perioden, förbi nu) ELLER backlog (schemalagt före perioden) — aldrig båda.
export const OVERDUE_BACKLOG_TOOLTIP =
  "Total öppen skuld, uppdelad i två ömsesidigt uteslutande delar. Overdue = schemalagt INOM perioden men förbi dagens datum. Backlog = schemalagt FÖRE perioden och fortfarande inte gjort (släpas in, syns ej i matrisen). Overdue mäts mot nu, så den delen är mest meningsfull för den pågående perioden.";
